import React, { useEffect, useState, useMemo, useCallback, useRef } from 'react';
import reservationsSvc from '../services/reservations';
import manageareas from '../services/manageareas';
import managetables from '../services/managetables';
import businessConfigSvc from '../services/businessConfig';
import { freeTablesFor, toMinutes } from '../services/seatingEngine';
import '../style/css/quickres2026.css';

/*
 * QuickReservation — the GLOBAL "הזמנת שולחן" modal (opened from the top navbar, available on
 * every page). Books a party for today and can HOLD one OR SEVERAL free tables (a big group
 * that needs multiple tables). Multi-table = one reservation per selected table (same guest),
 * since a reservation holds a single table server-side. Real DB writes via reservationCreate.
 */
const todayISO = () => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};
const nowHHMM = () => {
    const d = new Date();
    return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
};
const toMin = (hhmm) => {
    const [h, m] = String(hhmm || '').split(':').map(Number);
    return (Number(h) || 0) * 60 + (Number(m) || 0);
};

/*
 * parseAvailableTimes — turn the raw getAvailableTimes availability object into a SORTED list of
 * available "HH:MM" times for the current party P (+ optional area A). A slot is available when:
 *   - A set   → the area entry whose Value===A has ANY Capacity with quantity>0 AND Value>=P;
 *   - A unset → ANY area in that slot has such a capacity.
 * Fully guarded against null / malformed input.
 */
const parseAvailableTimes = (raw, party, areaId) => {
    if (!raw || !Array.isArray(raw.TimeSlots)) return [];
    const P = Math.max(1, Number(party) || 1);
    const A = areaId != null ? Number(areaId) : null;
    const areaHasFit = (area) => {
        if (!area || typeof area !== 'object') return false;
        const caps = area.Capacities;
        if (!caps || typeof caps !== 'object') return false;
        return Object.values(caps).some((c) => c && Number(c.quantity) > 0 && Number(c.Value) >= P);
    };
    const out = [];
    raw.TimeSlots.forEach((slot) => {
        if (!slot || typeof slot.Time !== 'string') return;
        const areasObj = slot.Areas;
        if (!areasObj || typeof areasObj !== 'object') return;
        const areaEntries = Object.values(areasObj);
        let ok;
        if (A != null) {
            ok = areaEntries.some((ar) => ar && Number(ar.Value) === A && areaHasFit(ar));
        } else {
            ok = areaEntries.some((ar) => areaHasFit(ar));
        }
        if (ok) {
            const hhmm = String(slot.Time).slice(0, 5); // "HH:MM:SS" → "HH:MM"
            // Only accept a real HH:MM — never turn a malformed time (e.g. "99:99") into a
            // clickable chip that would feed an invalid startTime into the booking submit.
            if (/^([01]\d|2[0-3]):[0-5]\d$/.test(hhmm)) out.push(hhmm);
        }
    });
    // de-dupe + sort by minutes
    return [...new Set(out)].sort((a, b) => toMin(a) - toMin(b));
};

export default function QuickReservation({ onClose, onCreated }) {
    const [areas, setAreas] = useState([]);
    const [tables, setTables] = useState([]);
    const [resRaw, setResRaw] = useState([]);
    const [seatDuration, setSeatDuration] = useState(90);
    const [form, setForm] = useState({ guestName: '', guestPhone: '', numberOfGuests: '2', startTime: nowHHMM(), area: '' });
    const [selectedTables, setSelectedTables] = useState(() => new Set()); // multi-table hold
    const [autoMode, setAutoMode] = useState(false); // let the server pick a best-fit free table
    const [saving, setSaving] = useState(false);
    const [msg, setMsg] = useState('');
    const [availTimes, setAvailTimes] = useState([]);   // available "HH:MM" times for party/area
    const [availLoading, setAvailLoading] = useState(false);
    // Selecting a table places a temporary HOLD so no one else can save it. { [tableId]: {holdId, token} }
    const [holds, setHolds] = useState({});
    const holdsRef = useRef(holds);
    useEffect(() => { holdsRef.current = holds; }, [holds]);
    const closedRef = useRef(false); // true once the modal is closing/unmounted
    // Release any still-active holds if the modal unmounts without an explicit close/confirm.
    useEffect(() => () => {
        closedRef.current = true;
        Object.values(holdsRef.current).forEach((h) => reservationsSvc.holdRelease(h.holdId, h.token));
    }, []);

    // Load everything needed to compute free tables when the modal opens.
    useEffect(() => {
        let alive = true;
        (async () => {
            const [{ rows }, areaList, tbls, cfg] = await Promise.all([
                reservationsSvc.listToday([]).catch(() => ({ rows: [] })),
                manageareas.getAllArea(true).catch(() => []),
                managetables.getAllTables(true).catch(() => []),
                businessConfigSvc.getConfig().catch(() => null),
            ]);
            if (!alive) return;
            setResRaw(Array.isArray(rows) ? rows : []);
            setAreas(Array.isArray(areaList) ? areaList : []);
            setTables(Array.isArray(tbls) ? tbls : []);
            if (cfg && cfg.maxTimeTableIsInUseInMinutes) setSeatDuration(businessConfigSvc.effectiveHold(cfg));
        })();
        return () => { alive = false; };
    }, []);

    const areaNameById = useMemo(() => {
        const m = new Map();
        areas.forEach((a) => m.set(Number(a.id || a.ID), a.name));
        return m;
    }, [areas]);

    // Free tables for the chosen time/party/area (only free — honors "רק מה שפנוי").
    const freeTables = useMemo(() => {
        if (!tables.length) return [];
        return freeTablesFor(resRaw, tables, {
            atMinutes: form.startTime ? toMinutes(form.startTime) : toMinutes('19:00'),
            party: Number(form.numberOfGuests) || 1,
            areaId: form.area ? Number(form.area) : null,
            durationMin: seatDuration,
        });
    }, [tables, resRaw, form.startTime, form.numberOfGuests, form.area, seatDuration]);

    // Fetch the server's AVAILABLE booking times for the current party/area (keyed on party+area,
    // and on mount). A seq/alive guard drops stale responses when the inputs change mid-flight.
    const availSeqRef = useRef(0);
    useEffect(() => {
        let alive = true;
        const seq = ++availSeqRef.current;
        setAvailLoading(true);
        (async () => {
            const raw = await reservationsSvc.getAvailableTimes({
                startingDate: todayISO(),
                countryCode: 'IL',
                areaID: form.area ? Number(form.area) : null,
            }).catch(() => null);
            if (!alive || seq !== availSeqRef.current) return;
            setAvailTimes(parseAvailableTimes(raw, form.numberOfGuests, form.area ? Number(form.area) : null));
            setAvailLoading(false);
        })();
        return () => { alive = false; };
    }, [form.numberOfGuests, form.area]);

    // The available time NEAREST to "now" (smallest |slotMinutes - nowMinutes|) — marked "הכי קרוב".
    const nearestTime = useMemo(() => {
        if (!availTimes.length) return null;
        const now = toMin(nowHHMM());
        return availTimes.reduce((best, t) => (
            Math.abs(toMin(t) - now) < Math.abs(toMin(best) - now) ? t : best
        ), availTimes[0]);
    }, [availTimes]);

    // Re-pull today's reservations (so the free-table list reflects what was just booked).
    const refreshRes = useCallback(async () => {
        const { rows } = await reservationsSvc.listToday([]).catch(() => ({ rows: [] }));
        setResRaw(Array.isArray(rows) ? rows : []);
    }, []);

    // When the time or area changes, previously-held tables may no longer be valid — RELEASE all
    // holds and clear the selection. (Runs on mount too, when holds is empty → no-op.)
    useEffect(() => {
        const current = holdsRef.current;
        if (Object.keys(current).length) {
            Object.values(current).forEach((h) => reservationsSvc.holdRelease(h.holdId, h.token));
            setHolds({});
            setSelectedTables(new Set());
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [form.startTime, form.area]);

    // Select → place a HOLD (lock the table); deselect → release it.
    const toggleTable = useCallback(async (id) => {
        const sid = String(id);
        if (selectedTables.has(sid)) {
            const h = holds[sid];
            setSelectedTables((prev) => { const n = new Set(prev); n.delete(sid); return n; });
            setHolds((prev) => { const n = { ...prev }; delete n[sid]; return n; });
            if (h) reservationsSvc.holdRelease(h.holdId, h.token);
            return;
        }
        const res = await reservationsSvc.hold({
            tableId: id, date: todayISO(), startTime: `${form.startTime}:00`,
            numberOfGuests: Math.max(1, Number(form.numberOfGuests) || 1),
        });
        // If the modal closed while the hold was in flight, release the returned hold and bail.
        if (closedRef.current) {
            if (res && res.ok && res.data && res.data.ID) reservationsSvc.holdRelease(res.data.ID, res.data.token);
            return;
        }
        if (res && res.ok && res.data && res.data.ID) {
            setHolds((prev) => ({ ...prev, [sid]: { holdId: res.data.ID, token: res.data.token } }));
            setSelectedTables((prev) => { const n = new Set(prev); n.add(sid); return n; });
        } else if (res && res.status === 409) { setMsg('השולחן נתפס כרגע ע"י מישהו אחר'); await refreshRes(); }
        else if (res && res.status === 418) { setMsg('העסק מלא בשעה זו'); }
        else { setMsg('לא ניתן לתפוס את השולחן — נסו שוב'); }
    }, [selectedTables, holds, form.startTime, form.numberOfGuests, refreshRes]);

    // Close the modal, releasing any un-confirmed holds first.
    const handleClose = useCallback(() => {
        closedRef.current = true;
        Object.values(holdsRef.current).forEach((h) => reservationsSvc.holdRelease(h.holdId, h.token));
        onClose();
    }, [onClose]);

    // Switching to AUTO mode → the manual holds no longer apply; release them + clear selection.
    const enableAuto = useCallback((on) => {
        setAutoMode(on);
        if (on) {
            Object.values(holdsRef.current).forEach((h) => reservationsSvc.holdRelease(h.holdId, h.token));
            setHolds({});
            setSelectedTables(new Set());
        }
    }, []);

    // Tell any open page (InvitesPage / LiveBoard) to refresh after a global booking.
    const notifyChanged = () => {
        try { window.dispatchEvent(new CustomEvent('hoppa:reservations-changed')); } catch (e) { /* ignore */ }
        if (onCreated) onCreated();
    };

    const submit = async (e) => {
        e.preventDefault();
        if (!form.guestName.trim() || saving) return;
        setSaving(true);
        setMsg('');
        const base = {
            date: todayISO(),
            startTime: `${form.startTime}:00`,
            numberOfGuests: Math.max(1, Number(form.numberOfGuests) || 1),
            guestName: form.guestName.trim(),
            guestPhone: form.guestPhone.trim(),
            area: Number(form.area) || 0,
        };
        // AUTOMATIC mode — the server picks a best-fit free table (one reservation).
        if (autoMode) {
            const r = await reservationsSvc.create({ ...base, autoTable: 1 });
            setSaving(false);
            if (r && r.ok) {
                notifyChanged();
                if (r.data && r.data.autoAssigned && Number(r.data.tableID) > 0) {
                    handleClose(); // a table was assigned → close (the list shows it)
                } else {
                    setMsg('נוספה — אך אין כרגע שולחן פנוי מתאים, אז ללא שולחן.');
                }
            } else {
                setMsg('לא ניתן לשמור — נסו שוב.');
            }
            return;
        }

        const tableIds = [...selectedTables].map(Number).filter((n) => n > 0);
        let ok = 0; const failedIds = []; let linkFailed = false;
        if (tableIds.length === 0) {
            const r = await reservationsSvc.create(base);
            if (r && r.ok) ok += 1;
        } else {
            // The selected tables are HELD (locked). CONFIRM each hold → a real booking, with the
            // party DISTRIBUTED across the tables (so covers aren't multiplied). Then LINK the group.
            const n = tableIds.length;
            const share = Math.max(1, Math.floor(base.numberOfGuests / n));
            const remainder = Math.max(0, base.numberOfGuests - share * n); // extra goes on the first table
            const confirmedIds = [];
            const stillHeld = {};
            for (let i = 0; i < n; i++) {
                const sid = String(tableIds[i]);
                const h = holds[sid];
                if (!h) { failedIds.push(sid); continue; }
                const partyForTable = share + (i === 0 ? remainder : 0);
                // eslint-disable-next-line no-await-in-loop
                const r = await reservationsSvc.holdConfirm({ holdId: h.holdId, token: h.token, guestName: base.guestName, guestPhone: base.guestPhone, numberOfGuests: partyForTable });
                if (r && r.ok) { ok += 1; confirmedIds.push(h.holdId); }
                else { failedIds.push(sid); stillHeld[sid] = h; }
            }
            // Confirmed holds are now real bookings — keep only the failed ones as active holds.
            setHolds(stillHeld);
            // Link the confirmed reservations into ONE big-group booking.
            if (confirmedIds.length >= 2) {
                const lk = await reservationsSvc.linkGroup(confirmedIds);
                linkFailed = !(lk && lk.ok);
            }
        }
        setSaving(false);
        if (ok > 0) notifyChanged();
        if (ok > 0 && failedIds.length === 0) {
            if (linkFailed) {
                // Reservations saved but the group link didn't land — say so (they're ungrouped).
                setMsg('ההזמנות נשמרו אך לא קושרו לקבוצה. אפשר לסגור; אם צריך, קשרו מהרשימה.');
                return;
            }
            handleClose();
            return;
        }
        if (failedIds.length > 0) {
            // Some/all held tables were taken meanwhile: keep ONLY the failed ones and refresh
            // availability so a retry never resubmits an already-booked/occupied table.
            setSelectedTables(new Set(failedIds));
            await refreshRes();
            const linkNote = linkFailed ? ' · ההזמנות שנשמרו לא קושרו לקבוצה' : '';
            setMsg((ok > 0
                ? `נשמרו ${ok} שולחנות · ${failedIds.length} נתפסו — בחרו אחרים ונסו שוב.`
                : `${failedIds.length} השולחנות נתפסו — בחרו אחרים ונסו שוב.`) + linkNote);
        } else {
            setMsg('לא ניתן לשמור — נסו שוב.');
        }
    };

    return (
        <div className="qr-overlay" onClick={handleClose}>
            <div className="qr-modal" role="dialog" aria-label="הזמנת שולחן" onClick={(e) => e.stopPropagation()}>
                <div className="qr-head">
                    <h3 className="qr-title">הזמנת שולחן</h3>
                    <button type="button" className="qr-close" onClick={handleClose} aria-label="סגור">✕</button>
                </div>
                <form className="qr-body" onSubmit={submit}>
                    <div className="qr-grid">
                        <label className="qr-field">שם האורח
                            <input className="qr-input" value={form.guestName} onChange={(e) => setForm({ ...form, guestName: e.target.value })} placeholder="שם מלא" autoFocus />
                        </label>
                        <label className="qr-field">טלפון
                            <input className="qr-input" dir="ltr" value={form.guestPhone} onChange={(e) => setForm({ ...form, guestPhone: e.target.value })} placeholder="050-0000000" />
                        </label>
                        <label className="qr-field">מס' סועדים
                            <input className="qr-input" type="number" min="1" max="60" value={form.numberOfGuests} onChange={(e) => setForm({ ...form, numberOfGuests: e.target.value })} />
                        </label>
                        <label className="qr-field">אזור
                            <select className="qr-input" value={form.area} onChange={(e) => setForm({ ...form, area: e.target.value })}>
                                <option value="">כל האזורים</option>
                                {areas.map((a) => <option key={a.id || a.ID} value={a.id || a.ID}>{a.name}</option>)}
                            </select>
                        </label>
                    </div>

                    {/* AVAILABLE TIMES — the primary way to pick a time: server-computed free slots
                        for this party/area, nearest-to-now marked "הכי קרוב". Clicking sets startTime. */}
                    <div className="qr-avail">
                        <div className="qr-avail-head">שעות פנויות</div>
                        {availLoading ? (
                            <div className="qr-avail-note">בודק זמינות…</div>
                        ) : availTimes.length === 0 ? (
                            <div className="qr-avail-note">אין שעות פנויות היום — אפשר להזין שעה ידנית</div>
                        ) : (
                            <div className="qr-avail-chips">
                                {availTimes.map((t) => {
                                    const pressed = form.startTime === t;
                                    const nearest = t === nearestTime;
                                    return (
                                        <button
                                            type="button"
                                            key={t}
                                            className={`qr-timechip ${pressed ? 'is-on' : ''} ${nearest ? 'is-near' : ''}`}
                                            aria-pressed={pressed}
                                            onClick={() => setForm((f) => ({ ...f, startTime: t }))}
                                        >
                                            {t}
                                            {nearest && <span className="qr-near-tag">הכי קרוב</span>}
                                        </button>
                                    );
                                })}
                            </div>
                        )}
                    </div>

                    {/* Manual time fallback — she can still type a time if the slot she wants isn't listed. */}
                    <label className="qr-field qr-time-manual">שעה (הזנה ידנית)
                        <input className="qr-input" type="time" value={form.startTime} onChange={(e) => setForm({ ...form, startTime: e.target.value })} />
                    </label>

                    <label className="qr-auto-toggle">
                        <input type="checkbox" checked={autoMode} onChange={(e) => enableAuto(e.target.checked)} />
                        שיבוץ אוטומטי — המערכת תבחר שולחן פנוי מתאים
                    </label>

                    {autoMode ? (
                        <div className="qr-auto-note">המערכת תשבץ אוטומטית את השולחן הפנוי הקטן ביותר שמתאים למספר הסועדים ולשעה.</div>
                    ) : (
                        <>
                            <div className="qr-tables-head">
                                בחרו שולחן/ות פנויים {selectedTables.size > 0 ? `· נבחרו ${selectedTables.size}` : '(לקבוצה גדולה — כמה שולחנות)'}
                            </div>
                            <div className="qr-tables">
                                {freeTables.length === 0 ? (
                                    <div className="qr-empty">אין שולחן פנוי בשעה זו</div>
                                ) : freeTables.map((t) => {
                                    const on = selectedTables.has(String(t.id));
                                    return (
                                        <button type="button" key={t.id} className={`qr-tchip ${on ? 'is-on' : ''} ${t.fits ? '' : 'is-tight'}`}
                                            onClick={() => toggleTable(t.id)}>
                                            שולחן {t.name} · {t.seats}{areaNameById.get(t.areaId) ? ` · ${areaNameById.get(t.areaId)}` : ''}
                                        </button>
                                    );
                                })}
                            </div>
                        </>
                    )}

                    {msg && <div className="qr-msg">{msg}</div>}

                    <div className="qr-actions">
                        <button type="button" className="qr-btn-ghost" onClick={handleClose}>ביטול</button>
                        <button type="submit" className="qr-btn-primary" disabled={saving || !form.guestName.trim()}>
                            {saving ? 'שומר…' : (autoMode ? 'שמירה (שיבוץ אוטומטי)' : (selectedTables.size > 1 ? `שמירת ${selectedTables.size} שולחנות` : 'שמירת הזמנה'))}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
}
